import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight } from "lucide-react";
import { IMG, LOGO } from "../lib/config";

const line = {
    initial: { y: "110%" },
    animate: (i = 0) => ({
        y: 0,
        transition: {
            duration: 1.1,
            ease: [0.7, 0, 0.15, 1],
            delay: 0.15 + i * 0.12,
        },
    }),
};

export default function Hero({ onBook }) {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
    const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-15%"]);

    return (
        <section ref={ref} className="relative md:min-h-[100svh] w-full overflow-hidden bg-powder pt-28 md:pt-28 pb-12 md:pb-0">
            {/* Editorial image — desktop only (absolute) */}
            <motion.div
                style={{ y: imgY, scale: imgScale }}
                className="hidden md:block absolute top-28 right-8 w-[46%] h-[82vh] z-0"
            >
                {/* Main frame */}
                <motion.div
                    initial={{ clipPath: "inset(100% 0 0 0)" }}
                    animate={{ clipPath: "inset(0% 0 0 0)" }}
                    transition={{ duration: 1.4, ease: [0.65, 0, 0.35, 1], delay: 0.25 }}
                    className="absolute inset-0 overflow-hidden"
                >
                    <img
                        src={IMG.hero}
                        alt="Personal training"
                        className="w-full h-full object-cover object-center"
                    />
                    <div className="absolute inset-0 bg-black/10 mix-blend-multiply" />
                </motion.div>
            </motion.div>

            <motion.div style={{ y: textY }} className="relative z-10 px-5 md:px-12 lg:px-16 max-w-[1600px] mx-auto">
                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1, duration: 0.8 }}
                    className="text-[11px] md:text-xs uppercase tracking-[0.28em] font-sans text-ink-muted mb-6 md:mb-10"
                >
                    <span className="inline-block w-8 h-px bg-fuchsia align-middle mr-3" />
                    Personal Trainer e Coach delle Gravidanze
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2, duration: 0.8 }}
                    className="flex flex-wrap gap-2 mb-8 md:mb-10"
                >
                    <span className="inline-flex items-center gap-2 rounded-full border border-ink/20 px-4 py-1.5 text-xs md:text-sm font-sans text-ink">
                        <span className="w-1.5 h-1.5 rounded-full bg-fuchsia" /> In presenza
                    </span>
                    <span className="inline-flex items-center gap-2 rounded-full border border-ink/20 px-4 py-1.5 text-xs md:text-sm font-sans text-ink">
                        <span className="w-1.5 h-1.5 rounded-full bg-fuchsia" /> Online, ovunque tu sia
                    </span>
                </motion.div>

                <h1 className="font-serif font-bold text-ink leading-[0.88] tracking-tight text-[15vw] md:text-[10vw] lg:text-[9.5vw]">
                    <span className="reveal-line">
                        <motion.span custom={0} variants={line} initial="initial" animate="animate">
                            La tua forza
                        </motion.span>
                    </span>
                    <span className="reveal-line">
                        <motion.span
                            custom={1}
                            variants={line}
                            initial="initial"
                            animate="animate"
                            className="italic text-fuchsia"
                        >
                            inizia
                        </motion.span>
                    </span>
                    <span className="reveal-line">
                        <motion.span custom={2} variants={line} initial="initial" animate="animate">
                            da qui.
                        </motion.span>
                    </span>
                </h1>

                <div className="mt-10 md:mt-14 grid md:grid-cols-[1fr_auto] gap-8 md:gap-16 items-end max-w-5xl">
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.4, duration: 0.7 }}
                        className="font-sans text-base md:text-lg text-ink-muted max-w-md leading-relaxed"
                    >
                        Allenamenti su misura per ogni corpo e ogni obiettivo — dal
                        fitness generale al percorso specializzato durante la gravidanza
                        e nel post parto.
                    </motion.p>
                    <motion.button
                        data-testid="hero-book-btn"
                        onClick={onBook}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.55, duration: 0.7 }}
                        className="btn-fuchsia self-start md:self-auto"
                    >
                        Prenota una consulenza gratuita, in presenza o online
                        <ArrowUpRight size={18} />
                    </motion.button>
                </div>

                {/* Mobile image — stacked below text */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4, duration: 0.9 }}
                    className="md:hidden mt-10 -mx-5 relative h-[52vh] overflow-hidden"
                >
                    <img src={IMG.hero} alt="Personal training" className="w-full h-full object-cover object-center" />
                    <div className="absolute inset-0 bg-black/10 mix-blend-multiply" />
                </motion.div>
            </motion.div>

        </section>
    );
}
